import axios, { AxiosError } from 'axios';
import { CORE_API, SESSION_GUARD } from 'config';
import AuthService, { AUTHORIZATION, AUTHORIZATION_EXPIRES_AT } from 'services/auth-service';
import { classifyFailure } from './classifyFailure';
import { reportSessionEvent, SessionEventContext } from './reportSessionEvent';

export type SessionVerdict = 'alive' | 'dead' | 'unknown';

export interface DeathInfo {
  reason: string;
  /** The token that was judged dead, so sign-out never removes a newer one. */
  token: string | undefined;
}

type Listener = () => void;
type DeathDetail = Omit<SessionEventContext, 'reason' | 'enforced'>;

/** Floor between probes, so a burst of failures cannot become a burst of probes. */
const MIN_PROBE_INTERVAL_MS = 15_000;
/** How long a verdict stays fresh enough to skip re-probing on focus. */
const STALE_RECHECK_MS = 5 * 60_000;
/** Escalating gaps after an inconclusive probe, for when Core is simply down. */
const UNKNOWN_BACKOFF_MS = [15_000, 30_000, 60_000, 300_000];
const PROBE_TIMEOUT_MS = 8_000;
const OTHER_TAB_SIGN_OUT = 'logged-out-in-another-tab';
/** What a probe rejected by Core reports. */
const PROBE_REJECTED_DETAIL: DeathDetail = { status: 401, host: 'core', coreVersion: 'v4' };
/**
 * How often a pending deadline is re-checked. A timer runs late by however long
 * the machine slept, and a tab that stayed focused gets no event on wake.
 */
const EXPIRY_RECHECK_MS = 30_000;

/**
 * A bare instance with **no** interceptor attached.
 *
 * This is what makes the monitor structurally loop-safe: a failing probe cannot
 * re-enter `reportSuspiciousFailure`, because nothing is watching this client
 * (axios 0.19 instances do not inherit the default instance's interceptors).
 * Do not pass it through `installSessionInterceptor`.
 */
const probeClient = axios.create({ timeout: PROBE_TIMEOUT_MS });

let inFlight: Promise<SessionVerdict> | null = null;
let lastProbeAt = 0;
let lastVerdict: SessionVerdict = 'alive';
let consecutiveUnknown = 0;
let dead = false;
let reportedDeath = false;
let deathInfo: DeathInfo | null = null;
/** The token a probe found dead. Report-only never latches, so stop probing it. */
let rejectedToken: string | undefined;
/** Bumped on reset, so a probe answered for a previous session is dropped. */
let generation = 0;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

const deadListeners = new Set<Listener>();

const currentToken = () => AuthService.getAuth()?.Authorization.token || undefined;

const isEnforcing = () => SESSION_GUARD.enforce;

const currentGate = () =>
  consecutiveUnknown > 0
    ? UNKNOWN_BACKOFF_MS[Math.min(consecutiveUnknown - 1, UNKNOWN_BACKOFF_MS.length - 1)]
    : MIN_PROBE_INTERVAL_MS;

/**
 * The single, idempotent "the session is over" trigger.
 *
 * `dead` is a one-way latch, so however many requests fail at once, listeners
 * fire exactly once per session. That is what guarantees a single sign-out
 * without the React layer needing to coordinate.
 */
const markDead = (
  reason: string,
  detail: DeathDetail = {},
  token = currentToken(),
  { force = false } = {}
) => {
  if (dead) {
    return;
  }

  // `force` is for a known expiry, which is authoritative: it ends the session
  // even in report-only mode.
  const enforced = force || isEnforcing();

  // Another tab signing out is an ordinary sign-out, not an expiry.
  if (!reportedDeath && reason !== OTHER_TAB_SIGN_OUT) {
    reportedDeath = true;
    try {
      reportSessionEvent('Session detected as expired', { ...detail, reason, enforced });
    } catch (error) {
      // Reporting is best-effort; it must not stop the sign-out.
    }
  }

  if (!enforced) {
    // Report-only: do not latch and do not notify, so behaviour is unchanged.
    return;
  }

  dead = true;
  lastVerdict = 'dead';
  deathInfo = { reason, token };
  deadListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // One bad listener must not stop the others from running.
    }
  });
};

/**
 * Ask Core whether `token` still works. Probing does not extend the session.
 *
 * The token goes in the Authorization header, never in the URL.
 */
const runProbe = async (token: string, probeGeneration: number): Promise<SessionVerdict> => {
  let verdict: SessionVerdict;

  try {
    const response = await probeClient.get(`${CORE_API.url}/v4/providers/me/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    verdict = response.status === 200 ? 'alive' : 'unknown';
  } catch (error) {
    // Everything but a 401 (no response, 5xx, 429) is inconclusive. Never
    // `dead`: an unreachable backend must not sign people out.
    verdict = (error as AxiosError)?.response?.status === 401 ? 'dead' : 'unknown';
  }

  // Answered for a session that is no longer the current one (a reset, or a
  // fresh sign-in meanwhile): the verdict says nothing about the new token.
  const sameSession = probeGeneration === generation;
  if (!sameSession || token !== currentToken()) {
    // Report-only never latches or notifies, so a token that died is still
    // worth reporting once it is gone: the boot's own sign-out removes it (and
    // resets the monitor) before the probe answers. A newer sign-in is not
    // reported against.
    if (verdict === 'dead' && !isEnforcing() && (sameSession || !currentToken())) {
      markDead('probe-rejected', PROBE_REJECTED_DETAIL, token);
    }
    return 'unknown';
  }

  lastProbeAt = Date.now();
  lastVerdict = verdict;
  consecutiveUnknown = verdict === 'unknown' ? consecutiveUnknown + 1 : 0;

  if (verdict === 'dead') {
    rejectedToken = token;
    markDead('probe-rejected', PROBE_REJECTED_DETAIL, token);
  }

  return verdict;
};

const verifySession = (): Promise<SessionVerdict> => {
  if (dead) {
    return Promise.resolve('dead');
  }

  const token = currentToken();
  if (!token) {
    // Nothing to verify. Not treated as an expiry: Authentication already
    // sends a visitor without credentials to the sign-in page.
    return Promise.resolve('unknown');
  }

  // Settled until reset (a sign-in or a sign-out): asking again cannot change it.
  if (token === rejectedToken) {
    return Promise.resolve('dead');
  }

  if (inFlight) {
    return inFlight;
  }

  if (lastProbeAt !== 0 && Date.now() - lastProbeAt < currentGate()) {
    return Promise.resolve(lastVerdict);
  }

  const probe: Promise<SessionVerdict> = runProbe(token, generation).finally(() => {
    if (inFlight === probe) {
      inFlight = null;
    }
  });
  inFlight = probe;
  return probe;
};

/**
 * Core vouched for the token within the last few minutes. An ordinary 401/403
 * (a permission the provider lacks) can repeat on every request, so it must not
 * buy a probe every 15s.
 */
const recentlyAlive = () =>
  lastVerdict === 'alive' && lastProbeAt !== 0 && Date.now() - lastProbeAt < STALE_RECHECK_MS;

const reportSuspiciousFailure = (error: AxiosError): void => {
  if (dead) {
    return;
  }

  // Not signed in: there is no session to lose. This also keeps failed sign-in
  // attempts from being read as expiries.
  if (!currentToken()) {
    return;
  }

  const { suspicion, host, status, coreVersion, code } = classifyFailure(error);

  if (suspicion === 'certain') {
    markDead('rejected-request', { status, host, coreVersion, code });
    return;
  }

  if (suspicion === 'probe' && !recentlyAlive()) {
    verifySession();
  }
};

const parseExpiry = (expiresAt: string | null | undefined) =>
  expiresAt ? Date.parse(expiresAt) : NaN;

const clearExpiryTimer = (): void => {
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
};

/**
 * Arms, or re-arms, a timer for the stored session's deadline; a past one ends
 * the session now. Each wake re-reads the deadline, since it may have changed:
 * a later one (a new sign-in) re-arms, none (signed out) is a no-op. The token
 * that ends is the current one, so a switched-to user is signed out too.
 */
const rearmStoredExpiry = (): void => {
  clearExpiryTimer();
  const expiresAtMs = parseExpiry(AuthService.getExpiresAt());
  if (Number.isNaN(expiresAtMs) || !currentToken()) {
    return;
  }

  const delay = expiresAtMs - Date.now();
  if (delay > 0) {
    expiryTimer = setTimeout(rearmStoredExpiry, Math.min(delay, EXPIRY_RECHECK_MS));
    return;
  }
  markDead('expired', {}, currentToken(), { force: true });
};

/** Fire-and-forget re-check, for boot and window focus. No-op without a token. */
const verifyIfStale = (): void => {
  if (dead) {
    return;
  }
  // Timers do not run while the machine sleeps: a deadline that passed
  // meanwhile ends the session now, not when the late timer fires.
  rearmStoredExpiry();
  if (dead || inFlight) {
    return;
  }
  if (lastProbeAt !== 0 && Date.now() - lastProbeAt < STALE_RECHECK_MS) {
    return;
  }
  if (!currentToken()) {
    return;
  }
  verifySession();
};

/**
 * Ends the session at a known deadline, rather than waiting for a rejected
 * request. Stored next to the session, so a reload (or another tab) re-arms it.
 */
const setExpiry = (expiresAt: string): void => {
  clearExpiryTimer();
  const expiresAtMs = parseExpiry(expiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return;
  }
  AuthService.setExpiresAt(new Date(expiresAtMs).toISOString());
  rearmStoredExpiry();
};

/**
 * Called on sign-in and once a dead session has been signed out. Keeps the
 * stored deadline: whatever session remains is re-armed from it.
 */
const reset = (): void => {
  generation += 1;
  dead = false;
  reportedDeath = false;
  deathInfo = null;
  rejectedToken = undefined;
  inFlight = null;
  lastProbeAt = 0;
  lastVerdict = 'alive';
  consecutiveUnknown = 0;
  rearmStoredExpiry();
};

export const sessionMonitor = {
  reportSuspiciousFailure,
  verifySession,
  verifyIfStale,
  reset,
  setExpiry,
  isSessionDead: () => dead,
  isEnforcing,
  getDeathInfo: () => deathInfo,
  // Unsubscribers return void, not Set.delete's boolean, so they can be returned
  // directly from a useEffect.
  onSessionDead(listener: Listener): () => void {
    deadListeners.add(listener);
    return () => {
      deadListeners.delete(listener);
    };
  },
  /** Exposed for tests and for the cross-tab listener. */
  markDead,
};

const storedToken = (raw: string | null) => {
  try {
    const token = JSON.parse(raw || 'null')?.auth_token;
    return typeof token === 'string' && token ? token : undefined;
  } catch (error) {
    return undefined;
  }
};

/**
 * Cross-tab sign-out. A tab's own `removeItem` never fires 'storage' in that
 * tab, so this only hears other tabs: one signing out, or storage being cleared.
 */
const onStorage = (event: StorageEvent) => {
  // Another tab signed in or out: follow its deadline.
  if (event.key === AUTHORIZATION_EXPIRES_AT) {
    rearmStoredExpiry();
    return;
  }

  const signedOutElsewhere =
    event.key === AUTHORIZATION
      ? event.newValue === null && !!storedToken(event.oldValue)
      : event.key === null && !currentToken();

  // Report-only: nothing to do, since this is neither reported nor latched.
  if (signedOutElsewhere && isEnforcing()) {
    markDead(OTHER_TAB_SIGN_OUT);
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', onStorage);
  // A reload must not drop the deadline of the session it restores.
  rearmStoredExpiry();
}
